import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ScoreBadge, EmptyState, heroSurfaceSx } from '../components/common';
import { buildDashboard } from '../services/analyticsService';
import { upcomingInterviews } from '../services/timelineService';
import { countDueQuestions } from '../services/reviewService';
import { useAppStore } from '../store/useAppStore';
import { useDbVersion } from '../db/events';

/** Compact stat used inside the hero — progress at a glance, not analytics. */
function HeroStat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  );
}

interface SetupStep {
  label: string;
  detail: string;
  to: string;
  done: boolean;
}

/**
 * First-run guide: walks new users through the setup sequence (profile →
 * school → questions → first practice) so the primary CTAs never lead to an
 * empty screen. Disappears once every step is done.
 */
function GettingStartedCard({ steps }: { steps: SetupStep[] }) {
  const doneCount = steps.filter((s) => s.done).length;
  const nextIndex = steps.findIndex((s) => !s.done);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Get set up</Typography>
          <Typography variant="body2" color="text.secondary">
            {doneCount} of {steps.length} done
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={(doneCount / steps.length) * 100}
          sx={{ height: 6, borderRadius: 3, mb: 2 }}
        />
        <Stack spacing={1}>
          {steps.map((s, i) => (
            <Stack
              key={s.label}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ opacity: s.done ? 0.65 : 1 }}
            >
              {s.done ? (
                <CheckCircleIcon color="success" sx={{ fontSize: 22 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ textDecoration: s.done ? 'line-through' : 'none' }}>
                  {s.label}
                </Typography>
                {!s.done && (
                  <Typography variant="caption" color="text.secondary">
                    {s.detail}
                  </Typography>
                )}
              </Box>
              {!s.done && (
                <Button
                  component={Link}
                  to={s.to}
                  size="small"
                  variant={i === nextIndex ? 'contained' : 'outlined'}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  {i === nextIndex ? 'Start' : 'Go'}
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const profile = useAppStore((s) => s.profile);
  const programs = useAppStore((s) => s.programs);
  const dbVersion = useDbVersion();
  // Recompute whenever any repository write happens (attempts, questions, …).
  // dbVersion isn't read inside the callbacks — it's a deliberate cache-bust key.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => buildDashboard(), [dbVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dueCount = useMemo(() => countDueQuestions(), [dbVersion]);
  const countdowns = useMemo(
    () =>
      upcomingInterviews(programs, {
        completedMocks: data.mockInterviewCount,
        weakCategories: data.weakCategories.map((c) => c.category),
      }),
    [programs, data],
  );

  const setupSteps: SetupStep[] = [
    {
      label: 'Build your interview profile',
      detail: 'Upload your application or add your materials — every answer is personalized from it.',
      to: '/profile',
      done: !!profile?.structured,
    },
    {
      label: 'Add a dental school',
      detail: 'Track each program you interview at, with its mission and interview style.',
      to: '/programs',
      done: programs.length > 0,
    },
    {
      label: 'Get interview questions',
      detail: 'Research real questions for your school, or paste your own.',
      to: '/research',
      done: data.totalQuestions > 0,
    },
    {
      label: 'Practice your first answer',
      detail: 'Record an answer and get structured, rubric-based feedback.',
      to: '/practice',
      done: data.totalAttempts > 0,
    },
  ];
  const setupComplete = setupSteps.every((s) => s.done);

  // "Next best action": the one thing the coach would tell you to do right
  // now. Priority: finish setup → clear due reviews → prep the nearest
  // interview → drill the weakest category → keep momentum.
  const nextInterview = countdowns[0];
  const focus = (() => {
    if (!setupComplete) {
      const step = setupSteps.find((s) => !s.done)!;
      return { title: step.label, detail: step.detail, to: step.to, label: 'Continue setup' };
    }
    if (dueCount > 0) {
      return {
        title: `${dueCount} answer${dueCount === 1 ? ' is' : 's are'} due for review`,
        detail: 'Spaced repetition works best on the day it is due — clear the queue while it is short.',
        to: '/practice?queue=due',
        label: 'Review now',
      };
    }
    if (nextInterview && nextInterview.daysLeft <= 14) {
      return {
        title:
          nextInterview.daysLeft === 0
            ? `${nextInterview.program.shortName} is TODAY`
            : `${nextInterview.program.shortName} in ${nextInterview.daysLeft} day${nextInterview.daysLeft === 1 ? '' : 's'}`,
        detail: 'Rehearse the questions this school actually asks, then skim your Interview Day briefing.',
        to: '/practice?queue=upcoming',
        label: 'Practice for this school',
      };
    }
    if (data.weakCategories.length > 0) {
      const w = data.weakCategories[0];
      return {
        title: `Strengthen “${w.category}”`,
        detail: `Your average here is ${w.averageScore} — a short, focused drill moves it fastest.`,
        to: `/practice?category=${encodeURIComponent(w.category)}&mode=drill`,
        label: 'Start a drill',
      };
    }
    return {
      title: 'Keep your momentum',
      detail: 'A couple of graded answers a day keeps your delivery sharp and your readiness climbing.',
      to: '/practice',
      label: 'Start practicing',
    };
  })();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Box>
      {/* Coaching hero: greeting, today's focus, and progress at a glance. */}
      <Paper elevation={0} sx={{ ...heroSurfaceSx, mb: 2.5 }}>
        <Grid container spacing={{ xs: 2.5, md: 4 }}>
          <Grid item xs={12} md={7}>
            <Typography variant="overline" color="text.secondary">
              {todayLabel}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.25 }}>
              {greeting}
              {profile ? `, ${profile.name.split(' ')[0]}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 560 }}>
              {setupComplete
                ? 'Practice out loud, get honest feedback, and walk into every interview already rehearsed.'
                : 'A few minutes of setup and every question, answer, and grade is personalized to you.'}
            </Typography>

            <Box
              sx={{
                mt: 2,
                p: { xs: 1.5, sm: 1.75 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: alpha(theme.palette.accent.main, 0.3),
                bgcolor: alpha(theme.palette.background.paper, 0.72),
              }}
            >
              <Typography variant="overline" sx={{ color: 'accent.dark' }}>
                Today's focus
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {focus.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {focus.detail}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button component={Link} to={focus.to} variant="contained" size="large" startIcon={<AllInclusiveIcon />}>
                {focus.label}
              </Button>
              {data.totalQuestions > 0 && (
                <Button component={Link} to="/mock" variant="outlined" startIcon={<RecordVoiceOverIcon />}>
                  Mock Interview
                </Button>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <ScoreBadge score={data.readiness} size={72} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary">
                  Readiness
                </Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                  {data.readinessLabel}
                </Typography>
                {nextInterview && (
                  <Typography variant="caption" color="text.secondary">
                    next interview: {nextInterview.program.shortName}
                    {nextInterview.daysLeft === 0 ? ' — today' : ` in ${nextInterview.daysLeft} day${nextInterview.daysLeft === 1 ? '' : 's'}`}
                  </Typography>
                )}
              </Box>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={data.readiness}
              sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
            />
            <Divider sx={{ my: 2 }} />
            <Stack
              direction="row"
              spacing={{ xs: 2.5, sm: 4 }}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <HeroStat
                label="Practiced"
                value={`${data.questionsPracticed}/${data.totalQuestions}`}
                sub={`${data.totalAttempts} attempt${data.totalAttempts === 1 ? '' : 's'}`}
              />
              <HeroStat
                label="Avg score"
                value={data.totalAttempts ? data.averageScore : '—'}
                sub="out of 100"
              />
              <HeroStat
                label="Due today"
                value={dueCount}
                sub={dueCount > 0 ? 'in review queue' : 'all clear'}
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {!setupComplete && (
          <Grid item xs={12}>
            <GettingStartedCard steps={setupSteps} />
          </Grid>
        )}

        {countdowns.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upcoming Interviews
                </Typography>
                <Grid container spacing={2}>
                  {countdowns.map((c) => (
                    <Grid item xs={12} md={6} key={c.program.id}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Chip
                          icon={<EventIcon />}
                          color={c.daysLeft <= 7 ? 'secondary' : 'default'}
                          label={c.daysLeft === 0 ? 'TODAY' : `${c.daysLeft} day${c.daysLeft === 1 ? '' : 's'}`}
                        />
                        <Box>
                          <Typography variant="subtitle2">
                            {c.program.name} — {c.date}
                          </Typography>
                          {c.plan.map((p, i) => (
                            <Typography key={i} variant="body2" color="text.secondary">
                              • {p}
                            </Typography>
                          ))}
                        </Box>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Score Trend
              </Typography>
              {data.trend.length ? (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.trend} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="index" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="score" stroke={theme.palette.text.disabled} strokeWidth={1} dot={false} name="Score" />
                      <Line type="monotone" dataKey="rolling" stroke={theme.palette.secondary.main} strokeWidth={3} dot={false} name="Rolling avg" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyState
                  title="No practice data yet"
                  hint="Answer a few questions in Infinite Practice to see your trend."
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weakness Detection
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {data.weaknessInsights.map((w, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main', mt: 0.8 }} />
                    <Typography variant="body2">{w}</Typography>
                  </Stack>
                ))}
              </Stack>
              {data.weakCategories.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2.5 }}>
                    Weakest categories
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {data.weakCategories.map((c) => (
                      <Chip key={c.category} size="small" label={`${c.category} · ${c.averageScore}`} />
                    ))}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Score by Category
              </Typography>
              {data.categoryScores.length ? (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {data.categoryScores.slice(0, 7).map((c) => (
                    <Box key={c.category}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">{c.category}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {c.averageScore} · {c.attempts}×
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={c.averageScore}
                        sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Practice to populate category scores.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Score by School
              </Typography>
              {data.scoreBySchool.length ? (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {data.scoreBySchool.map((s) => (
                    <Box key={s.school}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">{s.school}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.average} · {s.attempts}×
                        </Typography>
                      </Stack>
                      <LinearProgress
                        color="secondary"
                        variant="determinate"
                        value={s.average}
                        sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Practice school-specific questions to compare readiness by program.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
